import { Alert, Button, Divider, Dropdown, Form, Input, InputNumber, MenuProps, Modal, Radio, RadioChangeEvent, Select, Space, Spin, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, ReactNode, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import ILoan from '~Api/Loan/ILoan';
import ILoanPayoutFigure from '~Api/Loan/ILoanPayoutFigure';
import ILoanPayoutFigureItem from '~Api/Loan/ILoanPayoutFigureItem';
import ILoanPayoutFigureSection from '~Api/Loan/ILoanPayoutFigureSection';
import LoanPayoutFigureApprovalStatusEnum from '~Api/Loan/LoanPayoutFigureApprovalStatusEnum';
import LoanPayoutFigureSectionTypeEnum from '~Api/Loan/LoanPayoutFigureSectionTypeEnum';
import {
    loanGetAction,
    loanPayoutFigureDischargeDateAction,
    loanPayoutFigureGetAction,
    loanPayoutFigureItemAddAction,
    loanPayoutFigureItemDeleteAction,
    loanPayoutFigureItemValueSetAction,
    loanPayoutFigureReloadPrincipalLineItems,
    loanPayoutFigureSectionAddAction,
    loanPayoutFigureSectionDeleteAction,
    loanPayoutFigureValueSetAction,
} from '~Loans/actions';
import {
    loanPayoutFigureSelector,
    loanSelector,
} from '~Loans/selectors';
import {
    getLoanAppliedAccruedInterestTotal,
    getLoanPayoutFigureTotals,
} from '~Loans/utilities';
import { IGlobalState } from '~reducer';
import DatePicker from '~UI/DatePicker';
import { IDictionary } from '~utilities/IDictionary';
import Layout from './Layout';
import { currencyFormatter } from '~utilities/formatters';
import LoanPayoutFigurePrepaidInterestModeEnum from '~Api/Loan/LoanPayoutFigurePrepaidInterestModeEnum';
import { DownOutlined } from '@ant-design/icons';
import LoanPayoutFigureAccruedInterestModeEnum from '~Api/Loan/LoanPayoutFigureAccruedInterestModeEnum';
import LoanPayoutFigureMinimumTermInterestModeEnum from '~Api/Loan/LoanPayoutFigureMinimumTermInterestModeEnum';
import PayoutFigureInterestCalculatorModal from './PayoutFigureInterestCalculatorModal';
import DischargeInterestTypeEnum from '~Api/Application/DischargeInterestTypeEnum';
import { applicationValueSetAction } from '~Applications/actions';
import IApplication from '~Api/Application/IApplication';
import InterestMethodEnum from '~Api/Loan/InterestMethodEnum';

const defaultSections: LoanPayoutFigureSectionTypeEnum[] = [
    LoanPayoutFigureSectionTypeEnum.Principal,
    LoanPayoutFigureSectionTypeEnum.Interest,
    LoanPayoutFigureSectionTypeEnum.Other,
    LoanPayoutFigureSectionTypeEnum.Renewal,
    LoanPayoutFigureSectionTypeEnum.DefaultFees,
];

const sectionLabels: IDictionary<string> = {
    [LoanPayoutFigureSectionTypeEnum.Principal]: 'Principal',
    [LoanPayoutFigureSectionTypeEnum.Interest]: 'Interest',
    [LoanPayoutFigureSectionTypeEnum.Other]: 'Other (Inc GST)',
    [LoanPayoutFigureSectionTypeEnum.Renewal]: 'Renewal/Variation',
    [LoanPayoutFigureSectionTypeEnum.DefaultFees]: 'Default Fees',
};

export default function PayoutFigureEdit(): ReactElement {
    const { loanUuid, payoutFigureUuid } = useParams<{ loanUuid: string; payoutFigureUuid: string }>();

    const loan: ILoan = useSelector((state: IGlobalState): ILoan => loanSelector(state, loanUuid));
    const payoutFigure: ILoanPayoutFigure = useSelector((state: IGlobalState): ILoanPayoutFigure => loanPayoutFigureSelector(state, payoutFigureUuid));

    const [ dischargeDate, setDischargeDate ] = useState<string>(null);
    const [ dischargeInterestType, setDischargeInterestType ] = useState<string>(null);
    const [ isInterestCalculatorModalOpen, setIsInterestCalculatorModalOpen ] = useState<boolean>(false);
    const [ isMissingDetailsModalOpen, setIsMissingDetailsModalOpen ] = useState<boolean>(true);

    const dispatch: Dispatch = useDispatch();

    useEffect((): void => {
        if (!loan) {
            dispatch(loanGetAction(loanUuid));
        }
    }, [
        dispatch,
        loan,
        loanUuid,
    ]);

    useEffect((): void => {
        if (!payoutFigure) {
            dispatch(loanPayoutFigureGetAction(payoutFigureUuid));
        }
    }, [
        dispatch,
        payoutFigure,
        payoutFigureUuid,
    ]);

    const applicationValueSet: (applicationUuid: string, key: keyof IApplication, value: boolean | number | string) => void = useCallback((applicationUuid: string, key: keyof IApplication, value: boolean | number | string): void => {
        dispatch(applicationValueSetAction(applicationUuid, key, value));
    }, [
        dispatch,
    ]);

    const payoutFigureDischargeDate: (dischargeDate: string) => void = useCallback((dischargeDate: string): void => {
        dispatch(loanPayoutFigureDischargeDateAction(payoutFigureUuid, dischargeDate));
    }, [
        payoutFigureUuid,
        dispatch,
    ]);

    const payoutFigureItemAdd: (item: ILoanPayoutFigureItem) => void = useCallback((item: ILoanPayoutFigureItem): void => {
        dispatch(loanPayoutFigureItemAddAction(item));
    }, [
        dispatch,
    ]);

    const payoutFigureItemDelete: (itemUuid: string) => void = useCallback((itemUuid: string): void => {
        dispatch(loanPayoutFigureItemDeleteAction(itemUuid));
    }, [
        dispatch,
    ]);

    const payoutFigureItemValueSet: (itemUuid: string, key: keyof ILoanPayoutFigureItem, value: boolean | number | string) => void = useCallback((itemUuid: string, key: keyof ILoanPayoutFigureItem, value: boolean | number | string): void => {
        dispatch(loanPayoutFigureItemValueSetAction(itemUuid, key, value));
    }, [
        dispatch,
    ]);

    const payoutFigureReloadPrincipalLineItems: () => void = useCallback((): void => {
        dispatch(loanPayoutFigureReloadPrincipalLineItems(payoutFigureUuid));
    }, [
        payoutFigureUuid,
        dispatch,
    ]);

    const payoutFigureSectionAdd: (section: ILoanPayoutFigureSection) => void = useCallback((section: ILoanPayoutFigureSection): void => {
        dispatch(loanPayoutFigureSectionAddAction(section));
    }, [
        dispatch,
    ]);

    const payoutFigureSectionDelete: (sectionUuid: string) => void = useCallback((sectionUuid: string): void => {
        dispatch(loanPayoutFigureSectionDeleteAction(sectionUuid));
    }, [
        dispatch,
    ]);

    const payoutFigureValueSet: (key: keyof ILoanPayoutFigure, value: boolean | number | string) => void = useCallback((key: keyof ILoanPayoutFigure, value: boolean | number | string): void => {
        dispatch(loanPayoutFigureValueSetAction(payoutFigureUuid, key, value));
    }, [
        payoutFigureUuid,
        dispatch,
    ]);

    /**
     * Disables dates before the current payment date for the loan
     */
    const disabledDate: (date: Dayjs) => boolean = useCallback((date: Dayjs): boolean => {
        return loan.currentPaymentDate && date.format('YYYY-MM-DD') < loan.currentPaymentDate;
    }, [
        loan,
    ]);

    const onChangeItemAmount: (itemUuid: string, value: number) => void = useCallback((itemUuid: string, value: number): void => {
        payoutFigureItemValueSet(itemUuid, 'amount', value ? value : null);
    }, [
        payoutFigureItemValueSet,
    ]);

    const onChangeItemName: (itemUuid: string, event: React.ChangeEvent<HTMLInputElement>) => void = useCallback((itemUuid: string, event: React.ChangeEvent<HTMLInputElement>): void => {
        payoutFigureItemValueSet(itemUuid, 'name', event.target.value);
    }, [
        payoutFigureItemValueSet,
    ]);

    const onClickItemAdd: (sectionUuid: string) => void = useCallback((sectionUuid: string): void => {
        const item: ILoanPayoutFigureItem = {
            amount: null,
            name: null,
            payoutFigureSectionUuid: sectionUuid,
        };

        payoutFigureItemAdd(item);
    }, [
        payoutFigureItemAdd,
    ]);

    const onClickItemDelete: (itemUuid: string) => void = useCallback((itemUuid: string): void => {
        Modal.confirm({
            okText: 'Delete',
            onOk: (): void => {
                payoutFigureItemDelete(itemUuid);
            },
            title: 'Delete Line Item',
        });
    }, [
        payoutFigureItemDelete,
    ]);

    const onClickSectionAdd: (sectionType: LoanPayoutFigureSectionTypeEnum) => void = useCallback((sectionType: LoanPayoutFigureSectionTypeEnum): void => {
        const section: ILoanPayoutFigureSection = {
            items: [],
            name: sectionLabels[sectionType],
            payoutFigureUuid: payoutFigure.uuid,
            type: sectionType,
        };

        payoutFigureSectionAdd(section);
    }, [
        payoutFigure,
        payoutFigureSectionAdd,
    ]);

    const onClickSectionDelete: (sectionUuid: string) => void = useCallback((sectionUuid: string): void => {
        Modal.confirm({
            okText: 'Delete',
            onOk: (): void => {
                payoutFigureSectionDelete(sectionUuid);
            },
            title: 'Delete Section',
        });
    }, [
        payoutFigureSectionDelete,
    ]);

    const onClickMissingDetailsCancel: () => void = useCallback((): void => {
        setIsMissingDetailsModalOpen(false);
    }, []);

    const onClickMissingDetailsOk: () => void = useCallback((): void => {
        applicationValueSet(loan.applicationUuid, 'dischargeInterestType', dischargeInterestType);

        onClickMissingDetailsCancel();
    }, [
        applicationValueSet,
        dischargeInterestType,
        loan,
        onClickMissingDetailsCancel,
    ]);

    const onCloseInterestCalculatorModal: () => void = useCallback((): void => {
        setIsInterestCalculatorModalOpen(false);
    }, []);

    const onClickAutoCalculate: () => void = useCallback((): void => {
        setIsInterestCalculatorModalOpen(true);
    }, []);

    const onClickReloadPrincipalLineItems: () => void = useCallback((): void => {
        payoutFigureReloadPrincipalLineItems();
    }, [
        payoutFigureReloadPrincipalLineItems,
    ]);

    const onChangeBorrowerName: (event: React.ChangeEvent<HTMLInputElement>) => void = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
        payoutFigureValueSet('borrowerName', event.target.value);
    }, [
        payoutFigureValueSet,
    ]);

    const recalculateInterest: (dischargeDate: string) => void = useCallback((dischargeDate: string): void => {
        setDischargeDate(dischargeDate);

        payoutFigureDischargeDate(dischargeDate);
    }, [
        payoutFigureDischargeDate,
    ]);

    const onChangeDischargeDate: (date: Dayjs) => void = useCallback((date: Dayjs): void => {
        const dischargeDate: string = date.format('YYYY-MM-DD');

        recalculateInterest(dischargeDate);
    }, [
        recalculateInterest,
    ]);

    const onChangeLoanCode: (event: RadioChangeEvent) => void = useCallback((event: RadioChangeEvent): void => {
        payoutFigureValueSet('loanCode', event.target.value);
    }, [
        payoutFigureValueSet,
    ]);

    const onChangePrincipalAmount: (value: number) => void = useCallback((value: number): void => {
        payoutFigureValueSet('principalAmount', value ? value : 0);
    }, [
        payoutFigureValueSet,
    ]);

    const onChangeTrustAmount: (value: number) => void = useCallback((value: number): void => {
        payoutFigureValueSet('trustAmount', value ? value : 0);
    }, [
        payoutFigureValueSet,
    ]);

    const onChangePrepaidInterest: (value: number) => void = useCallback((value: number): void => {
        payoutFigureValueSet('prepaidInterest', value ? value : 0);
    }, [
        payoutFigureValueSet,
    ]);

    const onChangeAccruedInterestMode: (accruedInterestMode: LoanPayoutFigureAccruedInterestModeEnum) => void = useCallback((accruedInterestMode: LoanPayoutFigureAccruedInterestModeEnum): void => {
        payoutFigureValueSet('accruedInterestMode', accruedInterestMode);

        // We recalculate in the accrued  interest mode change due to legacy payout figures missing their calculations,
        recalculateInterest(payoutFigure.dischargeDate);
    }, [
        payoutFigure,
        payoutFigureValueSet,
        recalculateInterest,
    ]);

    const onChangeMinimumTermInterestMode: (minimumTermInterestMode: LoanPayoutFigureMinimumTermInterestModeEnum) => void = useCallback((minimumTermInterestMode: LoanPayoutFigureMinimumTermInterestModeEnum): void => {
        payoutFigureValueSet('minimumTermInterestMode', minimumTermInterestMode);

        // We recalculate in the minimum term interest mode change due to legacy payout figures missing their calculations,
        // In this case discharge interest type is not filled for existing applications which in turn can affect the calculation
        recalculateInterest(payoutFigure.dischargeDate);
    }, [
        payoutFigure,
        payoutFigureValueSet,
        recalculateInterest,
    ]);

    const onChangeDischargeInterestType: (dischargeInterestType: DischargeInterestTypeEnum) => void = useCallback((dischargeInterestType: DischargeInterestTypeEnum): void => {
        setDischargeInterestType(dischargeInterestType);
    }, []);

    const switchToPrepaidInterestAuto: () => void = useCallback((): void => {
        payoutFigureValueSet('prepaidInterestMode', LoanPayoutFigurePrepaidInterestModeEnum.Auto);

        // We recalculate in the prepaid interest mode change due to legacy payout figures missing their calculations
        recalculateInterest(payoutFigure.dischargeDate);
    }, [
        payoutFigure,
        payoutFigureValueSet,
        recalculateInterest,
    ]);

    const switchToPrepaidInterestManual: () => void = useCallback((): void => {
        payoutFigureValueSet('prepaidInterestMode', LoanPayoutFigurePrepaidInterestModeEnum.Manual);

        // We recalculate in the prepaid interest mode change due to legacy payout figures missing their calculations
        recalculateInterest(payoutFigure.dischargeDate);
    }, [
        payoutFigure,
        payoutFigureValueSet,
        recalculateInterest,
    ]);

    if (!loan || !payoutFigure) {
        return (
            <Layout uuid={loanUuid} section='payout-figure'>
                <Typography.Title level={2}>Payout Figure</Typography.Title>
                <Spin />
            </Layout>
        );
    }

    if (payoutFigure.approvalStatus !== LoanPayoutFigureApprovalStatusEnum.Draft) {
        return (
            <Layout uuid={loanUuid} section='payout-figure'>
                <Typography.Title level={2}>Payout Figure</Typography.Title>
                <Alert
                    message='Unable to edit this Payout Figure'
                    description='This Payout Figure is not in the Draft status and is unable to be edited.'
                    type='error'
                    showIcon={true}
                />
            </Layout>
        );
    }

    if (!loan.application.dischargeInterestType) {
        return (
            <Layout uuid={loanUuid} section='payout-figure'>
                <p>Please set the discharge interest type</p>
                <Modal
                    destroyOnClose={true}
                    okText='Confirm'
                    onCancel={onClickMissingDetailsCancel}
                    onOk={onClickMissingDetailsOk}
                    open={isMissingDetailsModalOpen}
                    title='Missing Details'
                >
                    <Form.Item label='Discharge Interest Type' className='discharge-interest-type'>
                        <Select onChange={onChangeDischargeInterestType}>
                            <Select.Option value={DischargeInterestTypeEnum.BreakCost}>Break Cost</Select.Option>
                            <Select.Option value={DischargeInterestTypeEnum.MinimumTerm}>Minimum Term</Select.Option>
                        </Select>
                    </Form.Item>
                </Modal>
            </Layout>
        );
    }

    const { balanceAmount, totalPayoutFigure } = getLoanPayoutFigureTotals(loan, payoutFigure);
    const appliedAccruedInterestTotal: number = getLoanAppliedAccruedInterestTotal(payoutFigure);

    const loanIs360DayInterestMethod: boolean = InterestMethodEnum.InterestMethod360 === loan.interestMethod;

    // Loans without the current payment date (the most recent interest inclusive loan transaction date or loan start date) cannot be calculated.
    const canAutoCalculateInterest: boolean = !!loan.currentPaymentDate;

    const dischargeDateCalculating: boolean = dischargeDate !== null && payoutFigure.dischargeDate !== dischargeDate;
    const hasPrincipalSection: boolean = payoutFigure.sections.some((loopSection: ILoanPayoutFigureSection): boolean => loopSection.type === LoanPayoutFigureSectionTypeEnum.Principal);
    const principalLineItemsLoading: boolean = payoutFigure.principalAmount === null;

    const prepaidInterestMenu: MenuProps = {
        items: [
            ...(payoutFigure.prepaidInterestMode === LoanPayoutFigurePrepaidInterestModeEnum.Manual ? [
                {
                    key: 'auto-calculate',
                    label: 'Auto Calculate',
                    onClick: switchToPrepaidInterestAuto,
                },
            ] : []),
            ...(payoutFigure.prepaidInterestMode === LoanPayoutFigurePrepaidInterestModeEnum.Auto ? [
                {
                    key: 'enter-amount',
                    label: 'Enter Amount',
                    onClick: switchToPrepaidInterestManual,
                },
            ] : []),
        ],
    };

    const prepaidInterestLabel: ReactNode = (
        <Dropdown menu={prepaidInterestMenu}>
            <a>Less Remaining Prepaid Interest <DownOutlined /></a>
        </Dropdown>
    );

    const prepaidInterestLabelBlock: ReactNode = canAutoCalculateInterest && loanIs360DayInterestMethod ? (
        <Form.Item className='dropdown-label' label={prepaidInterestLabel} />
    ) : (
        <span className='label'>Less Remaining Prepaid Interest</span>
    );

    const prepaidInterestAmountBlock: ReactNode = !dischargeDateCalculating ? (
        <span>{currencyFormatter.format(payoutFigure.prepaidInterestAuto)}</span>
    ) : (
        <Spin size='small' />
    );

    const prepaidInterestBlock: ReactNode = payoutFigure.prepaidInterestMode === LoanPayoutFigurePrepaidInterestModeEnum.Auto ? (
        <Space className='amount'>
            {prepaidInterestAmountBlock}
        </Space>
    ) : (
        <Form.Item>
            <InputNumber addonBefore='$' className='amount' onChange={onChangePrepaidInterest} value={payoutFigure.prepaidInterest} />
        </Form.Item>
    );

    const accruedInterestAmountBlock: ReactNode = payoutFigure.accruedInterestMode === LoanPayoutFigureAccruedInterestModeEnum.Auto && (
        <Space direction='vertical'>
            <Space className='line-item'>
                <span className='label'>{payoutFigure.accruedInterestLabel}</span>
                <Space className='amount'>
                    <span>{currencyFormatter.format(appliedAccruedInterestTotal)}</span>
                </Space>
            </Space>
        </Space>
    );

    const defaultInterestAmountBlock: ReactNode = payoutFigure.defaultInterest && (
        <Space direction='vertical'>
            <Space className='line-item'>
                <span className='label'>{payoutFigure.defaultInterestLabel}</span>
                <Space className='amount'>
                    <span>{currencyFormatter.format(payoutFigure.defaultInterest)}</span>
                </Space>
            </Space>
        </Space>
    );

    const calculatedInterestBlock: ReactNode = !dischargeDateCalculating ? (
        <>
            {accruedInterestAmountBlock}
            {defaultInterestAmountBlock}
        </>
    ) : (
        <Spin size='small' />
    );

    const sectionsBlock: ReactNode[] = _.map(defaultSections, (defaultSection: LoanPayoutFigureSectionTypeEnum): ReactNode => {
        if (defaultSection === LoanPayoutFigureSectionTypeEnum.Principal && !hasPrincipalSection) {
            return;
        }

        const section: ILoanPayoutFigureSection = _.find(payoutFigure.sections, (loopSection: ILoanPayoutFigureSection): boolean => loopSection.type === defaultSection);

        if (!section) {
            const onClickAdd: () => void = (): void => onClickSectionAdd(defaultSection);

            return (
                <React.Fragment key={sectionLabels[defaultSection]}>
                    <Divider />
                    <Space direction='vertical'>
                        <Space>
                            <strong>{sectionLabels[defaultSection]}</strong>
                            {defaultSection !== LoanPayoutFigureSectionTypeEnum.Principal && (<Button onClick={onClickAdd} size='small' type='link'>Add</Button>)}
                        </Space>
                    </Space>
                </React.Fragment>
            );
        }

        const itemsBlock: ReactNode[] = _.map(section.items, (item: ILoanPayoutFigureItem): ReactNode => {
            if (section.type === LoanPayoutFigureSectionTypeEnum.Principal) {
                return (
                    <Space className='line-item' key={item.uuid}>
                        <span className='label'>{item.name}</span>
                        <span className='amount'>{currencyFormatter.format(item.amount)}</span>
                    </Space>
                );
            }

            const onClickDelete: () => void = (): void => onClickItemDelete(item.uuid);
            const onChangeAmount: (value: number) => void = (value: number): void => onChangeItemAmount(item.uuid, value);
            const onChangeName: (event: React.ChangeEvent<HTMLInputElement>) => void = (event: React.ChangeEvent<HTMLInputElement>): void => onChangeItemName(item.uuid, event);

            return (
                <Space className='line-item' key={item.uuid}>
                    <Form.Item>
                        <Input className='label' onChange={onChangeName} value={item.name} />
                    </Form.Item>
                    <Form.Item>
                        <InputNumber addonBefore='$' className='amount' onChange={onChangeAmount} value={item.amount || null} />
                    </Form.Item>
                    <Button onClick={onClickDelete} size='small' type='link'>Delete</Button>
                </Space>
            );
        });

        const principalSectionSummaryBlock: ReactNode = (
            <Space className='line-item'>
                <span className='label'><strong>Principal Balance</strong></span>
                <span className='amount'><strong>{currencyFormatter.format(payoutFigure.principalAmount)}</strong></span>
            </Space>
        );
        const onClickAdd: () => void = (): void => onClickItemAdd(section.uuid);
        const onClickDelete: () => void = (): void => onClickSectionDelete(section.uuid);

        return (
            <React.Fragment key={section.name}>
                <Divider />
                <Space direction='vertical'>
                    <Space>
                        <strong>{section.name}</strong>
                        {[ LoanPayoutFigureSectionTypeEnum.DefaultFees, LoanPayoutFigureSectionTypeEnum.Renewal ].includes(section.type) && <Button onClick={onClickDelete} size='small' type='link'>Remove</Button>}
                        {section.type === LoanPayoutFigureSectionTypeEnum.Interest && canAutoCalculateInterest && loanIs360DayInterestMethod && <Button onClick={onClickAutoCalculate} size='small' type='link'>Auto Calculate</Button>}
                    </Space>
                    <React.Fragment>
                        {section.type === LoanPayoutFigureSectionTypeEnum.Interest && calculatedInterestBlock}
                        {itemsBlock}
                        {section.type === LoanPayoutFigureSectionTypeEnum.Principal && principalSectionSummaryBlock}
                    </React.Fragment>
                    {section.type !== LoanPayoutFigureSectionTypeEnum.Principal && (<Button onClick={onClickAdd}>Add Item</Button>)}
                </Space>
            </React.Fragment>
        );
    });

    const loanCodeBlock: JSX.Element = loan.salesforceCode && loan.code !== loan.salesforceCode && (
        <Form.Item label='Loan ID'>
            <Radio.Group onChange={onChangeLoanCode} value={payoutFigure.loanCode}>
                <Radio value={loan.code}>{loan.code}</Radio>
                <Radio value={loan.salesforceCode}>{loan.salesforceCode}</Radio>
            </Radio.Group>
        </Form.Item>
    );

    // If there is a current payment date, check if the discharge date is earlier.
    const isDischargeDateValid: boolean = (loan.currentPaymentDate && payoutFigure.dischargeDate >= loan.currentPaymentDate) || !loan.currentPaymentDate;

    const totalPayoutAmountBlock: ReactNode = !dischargeDateCalculating ? (
        <span>{currencyFormatter.format(totalPayoutFigure)}</span>
    ) : (
        <Spin size='small' />
    );

    const totalPayoutBlock: ReactNode = (
        <Space className='amount'>
            <span className='amount'><strong>{totalPayoutAmountBlock}</strong></span>
        </Space>
    );

    const balanceDueAmountBlock: ReactNode = !dischargeDateCalculating ? (
        <span>{currencyFormatter.format(balanceAmount)}</span>
    ) : (
        <Spin size='small' />
    );

    const balanceDueBlock: ReactNode = (
        <Space className='amount'>
            <span className='amount'><strong>{balanceDueAmountBlock}</strong></span>
        </Space>
    );

    const principalTransactionWarningBlock: JSX.Element = hasPrincipalSection && !principalLineItemsLoading && payoutFigure.principalAmount !== loan.amountRemaining && (
        <Alert
            className='principal-transaction-warning'
            message={<>Principal transactions have changed since this payout figure was created and must be updated. <a onClick={onClickReloadPrincipalLineItems}>Update now</a></>}
            type='error'
            showIcon={true}
        />
    );

    const legacyPrincipalAmountBlock: ReactNode = !hasPrincipalSection && (
        <>
            <Divider />
            <Space direction='vertical'>
                <Space className='line-item'>
                    <span className='label'>Principal</span>
                    <InputNumber addonBefore='$' className='amount' onChange={onChangePrincipalAmount} value={payoutFigure.principalAmount || null} />
                </Space>
            </Space>
        </>
    );

    const mainFormBlock: JSX.Element = (
        <>
            {legacyPrincipalAmountBlock}
            <React.Fragment>
                {sectionsBlock}
            </React.Fragment>
            <Divider />
            <Space direction='vertical'>
                <Space className='line-item'>
                    <span className='label'>Total Payout Figure</span>
                    {totalPayoutBlock}
                </Space>
                <Space className='line-item'>
                    {prepaidInterestLabelBlock}
                    {prepaidInterestBlock}
                </Space>
                <Space className='line-item'>
                    <span className='label'>Less Amount Held In Trust</span>
                    <Form.Item>
                        <InputNumber addonBefore='$' className='amount' onChange={onChangeTrustAmount} value={payoutFigure.trustAmount || null} />
                    </Form.Item>
                </Space>
                <Space className='line-item'>
                    <span className='label'><strong>Balance Due</strong></span>
                    {balanceDueBlock}
                </Space>
            </Space>
            <PayoutFigureInterestCalculatorModal
                isOpen={isInterestCalculatorModalOpen}
                onClose={onCloseInterestCalculatorModal}
                payoutFigure={payoutFigure}
                onChangeAccruedInterestMode={onChangeAccruedInterestMode}
                onChangeMinimumTermInterestMode={onChangeMinimumTermInterestMode}
            />
        </>
    );

    return (
        <Layout uuid={loanUuid} section='payout-figure'>
            <Typography.Title level={2}>Payout Figure</Typography.Title>
            {principalTransactionWarningBlock}
            {loanCodeBlock}
            <Form.Item className='borrower-name' label='Borrower'>
                <Input onChange={onChangeBorrowerName} value={payoutFigure.borrowerName} disabled={principalLineItemsLoading} />
            </Form.Item>
            <Form.Item
                className='discharge-date'
                help={!isDischargeDateValid && `Discharge date is earlier than the last loan payment for ${loan.code}`}
                label='Proposed Payout Date'
                validateStatus={!isDischargeDateValid && 'error'}
            >
                <DatePicker
                    disabledDate={disabledDate}
                    onChange={onChangeDischargeDate}
                    format='DD/MM/YYYY'
                    value={dayjs(dischargeDate ?? payoutFigure.dischargeDate)}
                    allowClear={false}
                    disabled={principalLineItemsLoading}
                />
            </Form.Item>
            {!principalLineItemsLoading ? mainFormBlock : (<Spin />)}
        </Layout>
    );

}
